import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Clock, Award, Users, BookOpen, GraduationCap } from 'lucide-react';
import RegistrationForm from './Zero2ExportRegistrationForm';

interface TrainingPackage {
  id: string;
  title: string;
  price: string;
  description: string;
  benefits: string[];
  color: 'blue' | 'green' | 'purple';
  featured?: boolean;
  icon: React.ComponentType<{ className?: string }>;
}

const packages: TrainingPackage[] = [
  {
    id: 'modular',
    title: 'Zero2Export™ Modular',
    price: '₦80,000',
    description: 'Register for a day whose modules pick your interest.',
    benefits: [
      'Learn from the modules that apply to you',
      'Q & A session',
      'Join the LSEM Community',
      'Get materials & resources',
      'Certificate of attendance/completion of modules',
      '9am - 1pm, either 24th or 25th July'
    ],
    color: 'blue',
    icon: BookOpen
  },
  {
    id: 'full',
    title: 'Zero2Export™',
    price: '₦160,000',
    description: 'Register for the complete Zero2Export™ training package.',
    benefits: [
      '2 days of intensive virtual training',
      'Complete Zero2Export™ curriculum',
      'Q & A session',
      'Mentorship after training',
      'Zero2Export™ Certification',
      'Materials & resources',
      '9am - 1pm, 24-25 July'
    ],
    color: 'green',
    featured: true,
    icon: Award
  },
  {
    id: 'plus',
    title: 'Zero2Export™ + BCEM',
    price: '₦310,000',
    description: "Fast-track to 'Basic Certificate in Export Management' instead of spending 3months.",
    benefits: [
      '3 days of intensive virtual training',
      'Complete Zero2Export™ curriculum with an executive fast-track to Basic Certificate in Export Management',
      'Zero2Export™ Certification',
      'Basic Certificate in Export Management',
      'Mentorship after training',
      'Alumni Network',
      'Materials & resources',
      '9am - 4pm, 24-26 July'
    ],
    color: 'purple',
    icon: GraduationCap
  }
];

const Zero2ExportPackage = () => {
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  const handleRegister = (packageId: string) => {
    setSelectedPackage(packageId);
    setIsRegistrationOpen(true);
  };

  const handleDownloadBrochure = () => {
    window.open('https://drive.google.com/file/d/1xWG-KxL7XB1wo3LoPPyRsQv5ht1diEdT/view?usp=sharing', '_blank');
  };

  return (
    <section id="zero2export-packages" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-900 sm:text-4xl"
          >
            Choose Your Package
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Select the package that best fits your export journey
          </motion.p>
        </div>

        {/* Package Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all hover:shadow-2xl ${
                pkg.featured ? 'ring-2 ring-blue-500 transform hover:scale-[1.02]' : ''
              }`}
            >
              {pkg.featured && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white px-4 py-1 rounded-bl-xl text-sm font-medium z-10">
                  Popular Choice
                </div>
              )}
              
              <div className="p-8 h-full flex flex-col">
                <div className={`w-16 h-16 bg-${pkg.color}-100 rounded-2xl flex items-center justify-center mb-6`}>
                  <pkg.icon className={`h-8 w-8 text-${pkg.color}-600`} />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.title}</h3>
                <p className="text-gray-600 mb-4">{pkg.description}</p>
                
                <div className="mb-6">
                  <span className="text-3xl font-bold text-gray-900">{pkg.price}</span>
                </div>

                <div className="space-y-4 mb-8 flex-grow">
                  {pkg.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className={`h-5 w-5 text-${pkg.color}-600 mr-3 flex-shrink-0 mt-0.5`} />
                      <span className="text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleRegister(pkg.id)}
                  className={`w-full py-4 bg-${pkg.color}-600 text-white rounded-xl font-semibold hover:bg-${pkg.color}-700 transition-colors duration-300`}
                >
                  Register Now
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Brochure Download */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-4">
            Need the modules to decide which day to pick for modular?
          </p>
          <button
            onClick={handleDownloadBrochure}
            className="text-blue-600 hover:text-blue-700 font-medium underline underline-offset-4 transition-colors"
          >
            Download Zero2Export&trade; Brochure
          </button>
        </motion.div>
      </div>

      {/* Registration Form Modal */}
      <RegistrationForm
        isOpen={isRegistrationOpen}
        onClose={() => setIsRegistrationOpen(false)}
        selectedPackage={selectedPackage}
      />
    </section>
  );
};

export default Zero2ExportPackage;